import {
  EXTRA_LOCATION_PRICE_PER_MONTH,
  EXTRA_SEAT_PRICE_PER_MONTH,
  LAUNCH_DISCOUNT,
  PLANS,
  extraPricePer,
  offerDiscount,
  pricePerYear,
  type InvitationOffer,
} from './plans';
import type { BillingInterval, PlanId } from './types';
import { regimenIvaDe, type TaxProfile } from './tax';
import { COUNTRY_TIMEZONE, esZonaHorariaValida } from './zonaHoraria';
import { mensajePlanEnPausa, planEnPausa } from './pausa';

/**
 * Fin de la prueba (sección 18 del lienzo de Alex, 15 oct 2026).
 *
 * Reglas puras que comparten la API (cerrojo, correos de los días 10 y 14) y
 * la web (etiqueta de arriba, aviso del Panel, «Elige tu plan»). Viven aquí
 * para que las dos capas cuenten lo mismo: si la web dice «Tu prueba terminó»
 * y la API deja guardar, o al revés, la persona no entiende nada.
 */

const DIA = 86_400_000;

/** Lo mínimo del tenant que hace falta para saber en qué punto está la prueba. */
export interface TenantEnPrueba {
  plan: PlanId;
  subscriptionStatus: string;
  trialEndsAt?: string;
  firstPaidAt?: string;
  internal?: boolean;
  founderPending?: boolean;
  founderTrial?: { endsAt: string };
  /** Zona del restaurante: la prueba termina a medianoche de SU hora, no de la de Londres. */
  timezone?: string;
  country?: string;
  /** Pausa del plan (retención al darse de baja). Ver pausa.ts. */
  pausedFrom?: string;
  pausedUntil?: string;
}

/* ------------------------------------------------------------------ */
/* Cuándo termina, en la hora del restaurante (bugbot 15 oct 2026)      */
/* ------------------------------------------------------------------ */

const SOLO_FECHA = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Zona con la que se cuenta la prueba: la del restaurante, la de su país o,
 * si no hay nada, la de Madrid (como la guía de primeros pasos).
 */
export function zonaDeLaPrueba(tenant: { timezone?: string; country?: string } | null | undefined): string {
  if (esZonaHorariaValida(tenant?.timezone)) return tenant!.timezone!;
  return COUNTRY_TIMEZONE[tenant?.country ?? ''] ?? 'Europe/Madrid';
}

/** El día (YYYY-MM-DD) que es en esa zona en ese instante. */
export function fechaEnZona(instante: number, zona: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: zona, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(instante));
}

/** Lo que la zona va por delante de UTC en ese instante, en milisegundos. */
function desfaseDeZona(instante: number, zona: string): number {
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: zona, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(instante));
  const v = (tipo: string) => Number(partes.find((p) => p.type === tipo)?.value);
  return Date.UTC(v('year'), v('month') - 1, v('day'), v('hour'), v('minute'), v('second')) - Math.floor(instante / 1000) * 1000;
}

/** El instante de las 00:00 de ese día en esa zona (con cambio de hora incluido). */
export function inicioDelDiaEnZona(fecha: string, zona: string): number {
  const [y, m, d] = fecha.slice(0, 10).split('-').map(Number);
  const comoUtc = Date.UTC(y, m - 1, d);
  const aprox = comoUtc - desfaseDeZona(comoUtc, zona);
  return comoUtc - desfaseDeZona(aprox, zona);
}

/**
 * Cuándo termina la prueba. `trialEndsAt` es un día (YYYY-MM-DD): el primer
 * día SIN prueba. Termina a las 00:00 de ese día en la hora del restaurante,
 * así que el último día de prueba es el anterior entero («Mañana, 29 de
 * octubre, termina tu prueba» sale el 28; «Tu prueba terminó el 29 oct»).
 *
 * Antes se comparaba con `Date.parse`, que da las 00:00 de Londres: en CDMX
 * el cerrojo caía a las 18:00 del último día. La extensión de 7 días guarda
 * un instante con hora: ese vale tal cual.
 */
export function finDeLaPrueba(tenant: { trialEndsAt?: string; timezone?: string; country?: string } | null | undefined): number {
  const fin = tenant?.trialEndsAt ?? '';
  if (SOLO_FECHA.test(fin)) return inicioDelDiaEnZona(fin, zonaDeLaPrueba(tenant));
  return Date.parse(fin);
}

/**
 * Días que quedan, contando hoy (el último día es «1»). Nunca negativo. Con un
 * día (YYYY-MM-DD) se cuentan días del calendario del restaurante: pasa la
 * zona (`zonaDeLaPrueba(tenant)`); sin ella, Madrid. Con un instante (la
 * extensión), redondeando hacia arriba.
 */
export function diasQueQuedanDePrueba(trialEndsAt: string | undefined, ahora: number = Date.now(), zona = 'Europe/Madrid'): number {
  const valor = trialEndsAt ?? '';
  if (SOLO_FECHA.test(valor)) {
    const hoy = fechaEnZona(ahora, zona);
    return Math.max(0, Math.round((Date.parse(valor) - Date.parse(hoy)) / DIA));
  }
  const fin = Date.parse(valor);
  if (!Number.isFinite(fin)) return 0;
  return Math.max(0, Math.ceil((fin - ahora) / DIA));
}

/**
 * ¿Terminó la prueba sin elegir plan? Entonces la cuenta VE TODO pero NO SUMA
 * (ni ventas automáticas, ni facturas, ni platos, ni Alexia).
 *
 * Fuera a propósito: quien ya pagó alguna vez (su caso es el impago o la
 * baja), las cuentas internas, la cortesía y los Founder pendientes, que
 * tienen su propio cerrojo (`founderLockGuard`). La extensión de 7 días
 * mueve `trialEndsAt`, así que mientras dura no cuenta como terminada.
 */
export function pruebaTerminadaSinPlan(tenant: TenantEnPrueba | null | undefined, ahora: number = Date.now()): boolean {
  if (!tenant || tenant.plan !== 'trial' || tenant.subscriptionStatus !== 'trialing') return false;
  if (tenant.firstPaidAt || tenant.internal || tenant.founderPending) return false;
  const fin = finDeLaPrueba(tenant);
  return Number.isFinite(fin) && fin <= ahora;
}

/**
 * ¿Se dio de baja? `subscription.deleted` deja la cuenta en `plan: 'trial'`
 * con `subscriptionStatus: 'canceled'`: sin esto, quien cancelaba volvía a
 * tener la prueba entera (y sus 50 escaneos y 50 mensajes) para siempre.
 * Fuera: la cortesía, las internas y los Founder pendientes (su cerrojo).
 */
export function planCancelado(tenant: TenantEnPrueba | null | undefined): boolean {
  if (!tenant || tenant.subscriptionStatus !== 'canceled') return false;
  return tenant.plan !== 'cortesia' && !tenant.internal && !tenant.founderPending;
}

/**
 * ¿La cuenta está sin plan? Prueba terminada sin elegir plan, o plan
 * cancelado. Las dos igual: VE TODO pero NO SUMA, con «Elige tu plan». Es la
 * puerta que usan la API (impagoGuard, assertTenantWriteAccess, la
 * sincronización nocturna, el correo de facturas), la web y la demo.
 */
export function sinPlanActivo(tenant: TenantEnPrueba | null | undefined, ahora: number = Date.now()): boolean {
  // Y el plan en pausa (retención, 18 oct 2026): igual, VE TODO pero NO SUMA, con su propio texto.
  return pruebaTerminadaSinPlan(tenant, ahora) || planCancelado(tenant) || planEnPausa(tenant, ahora);
}

/**
 * En qué punto de la prueba está:
 * - `prueba`: los primeros días, etiqueta verde «te quedan N días».
 * - `aviso`: desde el día 10 (quedan 4 o menos): aviso en el Panel y correo.
 * - `ultimo-dia`: queda 1 día. La etiqueta pasa a ámbar.
 * - `terminada`: terminó sin plan. Ámbar, «Tu prueba terminó el …».
 * Sin prueba (paga, cortesía, Founder sin prueba), `null`.
 */
export type FasePrueba = 'prueba' | 'aviso' | 'ultimo-dia' | 'terminada';

export function fasePrueba(tenant: TenantEnPrueba | null | undefined, ahora: number = Date.now()): FasePrueba | null {
  if (!tenant || tenant.subscriptionStatus !== 'trialing' || !tenant.trialEndsAt) return null;
  if (tenant.founderPending && !tenant.founderTrial) return null;
  if (pruebaTerminadaSinPlan(tenant, ahora)) return 'terminada';
  const dias = diasQueQuedanDePrueba(tenant.trialEndsAt, ahora, zonaDeLaPrueba(tenant));
  if (dias === 0) return 'terminada';
  if (dias === 1) return 'ultimo-dia';
  if (dias <= 4) return 'aviso';
  return 'prueba';
}

/**
 * La fase para el aviso del Panel (FinPruebaAviso): igual que `fasePrueba`,
 * pero «terminada» solo si de verdad la cuenta se quedó sin plan. Una cuenta
 * interna, una que ya pagó o un Founder pendiente con `trialEndsAt` pasado no
 * ven «Tu prueba terminó» ni los botones apagados (bugbot 15 oct 2026).
 */
export function faseDelAvisoDePrueba(tenant: TenantEnPrueba | null | undefined, ahora: number = Date.now()): FasePrueba | null {
  const fase = fasePrueba(tenant, ahora);
  if (fase === 'terminada' && !pruebaTerminadaSinPlan(tenant, ahora)) return null;
  return fase;
}

/* ------------------------------------------------------------------ */
/* Qué le recomendamos                                                 */
/* ------------------------------------------------------------------ */

/** Lo que usó en la prueba: de aquí sale la recomendación. */
export interface UsoDePrueba {
  usuarios: number;
  locales: number;
  escaneos: number;
  mensajes: number;
  /** Días de prueba que ya pasaron (1–14): para proyectar al mes. */
  diasUsados: number;
}

/** Lo que haría en un mes al ritmo de la prueba. */
export function proyectarAlMes(cantidad: number, diasUsados: number): number {
  const dias = Math.max(1, Math.min(31, Math.round(diasUsados)));
  return Math.round((Math.max(0, cantidad) * 30) / dias);
}

export interface Recomendacion {
  plan: 'basic' | 'pro';
  /** «1 usuario, 1 local, 31 escaneos y 48 mensajes. Con Básico te sobra. …» */
  frase: string;
}

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

/**
 * Básico salvo que lo que usó no quepa: más de un local, más de una persona,
 * o más escaneos o mensajes de los del Básico al ritmo de la prueba.
 * Mejor recomendar lo justo que empujar a Pro y que cancele al mes (Alex).
 */
export function recomendarPlan(uso: UsoDePrueba): Recomendacion {
  const basico = PLANS.basic;
  const escaneosMes = proyectarAlMes(uso.escaneos, uso.diasUsados);
  const mensajesMes = proyectarAlMes(uso.mensajes, uso.diasUsados);
  const resumen = `${plural(uso.usuarios, 'usuario', 'usuarios')}, ${plural(uso.locales, 'local', 'locales')}, ${plural(uso.escaneos, 'escaneo', 'escaneos')} y ${plural(uso.mensajes, 'mensaje', 'mensajes')}.`;
  const motivos: string[] = [];
  if (uso.usuarios > basico.maxUsers) motivos.push(`Básico es para ${plural(basico.maxUsers, 'usuario', 'usuarios')}`);
  if (uso.locales > basico.maxLocations) motivos.push(`Básico trae ${plural(basico.maxLocations, 'local', 'locales')}`);
  if (escaneosMes > basico.receiptScansPerMonth) motivos.push(`a este ritmo harías unos ${escaneosMes} escaneos al mes y Básico trae ${basico.receiptScansPerMonth}`);
  const mensajesBasico = basico.assistantMessagesPerMonth ?? Infinity;
  if (mensajesMes > mensajesBasico) motivos.push(`a este ritmo serían unos ${mensajesMes} mensajes al mes y Básico trae ${mensajesBasico}`);
  if (motivos.length) {
    const porque = motivos.join('; ');
    return { plan: 'pro', frase: `${resumen} Te conviene Pro: ${porque.charAt(0).toLowerCase()}${porque.slice(1)}.` };
  }
  return {
    plan: 'basic',
    frase: `${resumen} Con Básico te sobra. Si mañana entra tu encargado, cambias a Pro en un clic.`,
  };
}

/* ------------------------------------------------------------------ */
/* Impuesto de la suscripción y resumen de «Hoy pagas»                 */
/* ------------------------------------------------------------------ */

/**
 * IVA de la suscripción, como lo hace hoy la facturación (Stripe Tax con
 * `automatic_tax`, ver billing.ts y memoria «facturacion-iva»): 21 % en
 * España y a quien está en la UE sin NIF-IVA; 0 % a una empresa de la UE con
 * NIF-IVA (inversión del sujeto pasivo) y fuera de la UE. Es lo que se
 * enseña ANTES de pagar; el importe final lo calcula Stripe en el cobro.
 */
export function ivaDeLaSuscripcion(tax: TaxProfile | undefined, paisNegocio: string): { pct: number; etiqueta: string } {
  const regimen = regimenIvaDe(tax ?? {}, paisNegocio);
  if (regimen === 'nacional' || regimen === 'intracomunitario_consumidor') return { pct: 21, etiqueta: 'IVA (21 %)' };
  if (regimen === 'intracomunitario_empresa') return { pct: 0, etiqueta: 'IVA (inversión del sujeto pasivo)' };
  return { pct: 0, etiqueta: 'Impuestos (0 %, fuera de la UE)' };
}

const redondear = (n: number) => Math.round(n * 100) / 100;

/** Suma meses o años a una fecha YYYY-MM-DD sin pasarse de mes (31 ene + 1 mes = 28 feb). */
export function sumarMeses(iso: string, meses: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const destino = new Date(Date.UTC(y, m - 1 + meses, 1));
  const ultimo = new Date(Date.UTC(destino.getUTCFullYear(), destino.getUTCMonth() + 1, 0)).getUTCDate();
  destino.setUTCDate(Math.min(d, ultimo));
  return destino.toISOString().slice(0, 10);
}

export interface ResumenDeCompra {
  plan: 'basic' | 'pro';
  intervalo: BillingInterval;
  /** «Básico mensual», «Pro anual». */
  lineaPlan: string;
  /** Precio de lista del periodo, sin impuestos. */
  precio: number;
  /** Descuento del primer cobro (negativo o 0). */
  descuento: number;
  /** Qué descuento es, para la línea del resumen. */
  etiquetaDescuento?: string;
  base: number;
  ivaPct: number;
  iva: number;
  /** «Hoy pagas». */
  total: number;
  /** Lo que pagará después y desde cuándo (YYYY-MM-DD). */
  despues: { importe: number; desde: string; renovacion: boolean };
  /**
   * Locales y personas que tenía en la prueba y no caben en el plan: se
   * cobran como extras para que siga como lo dejó (bugbot 15 oct 2026).
   */
  lineasExtra: LineaExtraDeCompra[];
  /** Meses con el precio rebajado (0 sin descuento; 12 en anual con oferta). */
  mesesConDescuento: number;
}

export interface LineaExtraDeCompra {
  tipo: 'local' | 'persona';
  /** «1 local extra», «2 personas extra». */
  etiqueta: string;
  cantidad: number;
  /** Precio de lista del periodo, sin impuestos. */
  importe: number;
}

/**
 * Lo que no cabe en el plan al salir de la prueba: la prueba trae 2 locales
 * y 5 personas; Básico, 1 y 1. Antes se pagaba Básico y se quedaba con dos
 * locales sin pagar el segundo. Ahora el Checkout suma lo que falte («sigues
 * como lo dejaste»), que es mejor que obligar a desactivar nada. Los locales
 * regalados desde /admin (`bonusLocations`) no se cobran.
 */
export function extrasParaSeguir(
  plan: PlanId,
  uso: { locales: number; usuarios: number },
  regalados: { bonusLocations?: number } = {},
): { locales: number; personas: number } {
  const localesIncluidos = (PLANS[plan]?.maxLocations ?? 1) + Math.max(0, Math.floor(regalados.bonusLocations ?? 0));
  const personasIncluidas = PLANS[plan]?.maxUsers ?? 1;
  return {
    locales: Math.max(0, Math.floor(uso.locales) - localesIncluidos),
    personas: Math.max(0, Math.floor(uso.usuarios) - personasIncluidas),
  };
}

/**
 * El resumen de la pantalla «Elige tu plan»: precio, −50 % los 3 primeros
 * meses (solo mensual), impuesto y «Hoy pagas»; debajo, cuánto y cuándo.
 * Con la oferta por invitación (ALEXFAMILY) manda la oferta: no se acumulan.
 */
export function resumenDeCompra(input: {
  plan: 'basic' | 'pro';
  intervalo: BillingInterval;
  ivaPct: number;
  /** Día del cobro, YYYY-MM-DD. */
  hoy: string;
  oferta?: InvitationOffer | null;
  /** Locales y personas extra que hay que añadir (`extrasParaSeguir`). */
  extras?: { locales?: number; personas?: number };
}): ResumenDeCompra {
  const { plan, intervalo, ivaPct, hoy } = input;
  const anual = intervalo === 'year';
  const oferta = input.oferta && input.oferta.plan === plan ? input.oferta : null;
  const precio = anual ? pricePerYear(plan) : PLANS[plan].pricePerMonth;
  const lineasExtra: LineaExtraDeCompra[] = [];
  const locales = Math.max(0, Math.floor(input.extras?.locales ?? 0));
  const personas = Math.max(0, Math.floor(input.extras?.personas ?? 0));
  if (locales) {
    lineasExtra.push({ tipo: 'local', etiqueta: `${locales} ${locales === 1 ? 'local extra' : 'locales extra'}`, cantidad: locales, importe: redondear(extraPricePer(EXTRA_LOCATION_PRICE_PER_MONTH, intervalo) * locales) });
  }
  if (personas) {
    lineasExtra.push({ tipo: 'persona', etiqueta: `${personas} ${personas === 1 ? 'persona extra' : 'personas extra'}`, cantidad: personas, importe: redondear(extraPricePer(EXTRA_SEAT_PRICE_PER_MONTH, intervalo) * personas) });
  }
  const lista = redondear(precio + lineasExtra.reduce((s, l) => s + l.importe, 0));
  let descuento = 0;
  let etiquetaDescuento: string | undefined;
  let mesesConDescuento = 0;
  if (oferta) {
    // El cupón de la oferta va solo sobre el producto Pro: los extras, a precio de lista.
    descuento = -offerDiscount(oferta, intervalo);
    etiquetaDescuento = `Oferta ${oferta.name}`;
    mesesConDescuento = anual ? 12 : oferta.months;
  } else if (!anual) {
    // El cupón de lanzamiento no está limitado a un producto: rebaja también los extras.
    descuento = -redondear(lista * (LAUNCH_DISCOUNT.percent / 100));
    etiquetaDescuento = `−${LAUNCH_DISCOUNT.percent} % los ${LAUNCH_DISCOUNT.months} primeros meses`;
    mesesConDescuento = LAUNCH_DISCOUNT.months;
  }
  const base = redondear(lista + descuento);
  const iva = redondear(base * (ivaPct / 100));
  const desde = anual ? sumarMeses(hoy, 12) : sumarMeses(hoy, Math.max(1, mesesConDescuento));
  return {
    plan,
    intervalo,
    lineaPlan: `${PLANS[plan].name} ${anual ? 'anual' : 'mensual'}`,
    precio,
    descuento,
    ...(etiquetaDescuento ? { etiquetaDescuento } : {}),
    base,
    ivaPct,
    iva,
    total: redondear(base + iva),
    despues: { importe: lista, desde, renovacion: anual || mesesConDescuento === 0 },
    lineasExtra,
    mesesConDescuento,
  };
}

/* ------------------------------------------------------------------ */
/* Lo que consiguió en la prueba (aviso del Panel y correos)           */
/* ------------------------------------------------------------------ */

/** Lo que la API calcula de la cuenta para el aviso, los correos y «Elige tu plan». */
export interface ResumenPrueba {
  restaurante: string;
  /** Nombre de pila de quien recibe el aviso, si lo sabemos. */
  nombre?: string;
  trialEndsAt?: string;
  diasQueQuedan: number;
  fase: FasePrueba | null;
  /** Platos de la carta con su costo completo (sin precios estimados). */
  platosCosteados: number;
  /** El plato que más deja en el local, en la moneda del restaurante. */
  platoEstrella?: { nombre: string; dejaLocal: number };
  /** Facturas guardadas (no notas). */
  facturas: number;
  /** Lo que le quedó este mes, si hay ventas y gastos con los que decirlo. */
  resultado?: { desde: string; hasta: string; ventas: number; teQuedo: number };
  /** Días con ventas guardadas (a mano, Excel o punto de venta). */
  diasConVentas: number;
  /** Nombre del punto de venta conectado, si hay. */
  puntoDeVenta?: string;
  uso: UsoDePrueba;
  recomendacion: Recomendacion;
  /** Moneda del restaurante (importes de platos y resultado). */
  moneda: string;
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** «29 de octubre». */
export function diaYMes(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${d} de ${MESES[m - 1] ?? ''}`;
}

/** «29 oct», para la etiqueta de arriba. */
export function diaYMesCorto(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${d} ${MESES_CORTOS[m - 1] ?? ''}`;
}

/** «Del 1 al 27 de octubre» (o «Del 28 de septiembre al 3 de octubre»). */
export function rangoDeDias(desde: string, hasta: string): string {
  if (desde.slice(0, 7) === hasta.slice(0, 7)) {
    return desde === hasta ? `El ${diaYMes(hasta)}` : `Del ${Number(desde.slice(8, 10))} al ${diaYMes(hasta)}`;
  }
  return `Del ${diaYMes(desde)} al ${diaYMes(hasta)}`;
}

/** La fecha en que terminó (o termina) la prueba: el día de `trialEndsAt`. */
export function diaFinDePrueba(trialEndsAt: string): string {
  return trialEndsAt.slice(0, 10);
}

/**
 * Las tres cosas que consiguió CON SUS DATOS. Solo lo que existe: si no
 * costeó platos, no se habla de platos (Alex: «si no costeó, no se dice
 * 14 platos»).
 */
export function logrosDePrueba(r: Pick<ResumenPrueba, 'platosCosteados' | 'platoEstrella' | 'facturas' | 'resultado'>, dinero: (n: number) => string): string[] {
  const logros: string[] = [];
  if (r.platosCosteados > 0) {
    const estrella = r.platoEstrella && r.platoEstrella.dejaLocal > 0
      ? ` ${r.platoEstrella.nombre.charAt(0).toUpperCase()}${r.platoEstrella.nombre.slice(1)} te deja ${dinero(r.platoEstrella.dejaLocal)} en el local.`
      : '';
    logros.push(`Costeaste ${plural(r.platosCosteados, 'plato', 'platos')}.${estrella}`);
  }
  if (r.facturas > 0) logros.push(`Subiste ${plural(r.facturas, 'factura', 'facturas')}: tus precios están al día.`);
  // Solo si le quedó algo: con un mes a medias (sin recuento, gastos del mes
  // entero) una pérdida suele ser un dato incompleto, y el aviso habla de lo
  // que ya tiene, no de lo que pierde (Alex).
  if (r.resultado && r.resultado.ventas > 0 && r.resultado.teQuedo > 0) {
    logros.push(`${rangoDeDias(r.resultado.desde, r.resultado.hasta)} te quedaron unos ${dinero(Math.round(r.resultado.teQuedo))}.`);
  }
  return logros;
}

/**
 * Lo que le falta de «Para ver tus números» (plato → factura → ventas). Con
 * poco uso el aviso se lo recuerda en vez de presumir de lo que no hay.
 */
export function loQueFaltaParaTusNumeros(r: Pick<ResumenPrueba, 'platosCosteados' | 'facturas' | 'diasConVentas'>): string[] {
  const faltan: string[] = [];
  if (r.platosCosteados === 0) faltan.push('Costear tu plato estrella');
  if (r.facturas === 0) faltan.push('Subir tu primera factura');
  if (r.diasConVentas === 0) faltan.push('Traer tus ventas');
  return faltan;
}

/** ¿Usó poco la prueba? Menos de dos de las tres cosas grandes. */
export function pocoUso(r: Pick<ResumenPrueba, 'platosCosteados' | 'facturas' | 'diasConVentas'>): boolean {
  return loQueFaltaParaTusNumeros(r).length >= 2;
}

/* ------------------------------------------------------------------ */
/* Sin plan: qué se dice al tocar un botón de crear                    */
/* ------------------------------------------------------------------ */

/**
 * «Para subir facturas, elige un plan»: la frase según la pantalla (o la
 * ruta de la API) donde la persona quiso sumar algo.
 */
export function fraseSinPlan(ruta: string): string {
  const r = ruta.toLowerCase();
  if (/(gasto|expense|receipt|factura|escane|inbound|proveedor|supplier|pagar|payable)/.test(r)) return 'Para subir facturas, elige un plan';
  if (/(venta|ingreso|revenue|integration|\bpos\b|dish-sales|corte|anotar)/.test(r)) return 'Para anotar ventas, elige un plan';
  if (/(alexia|assistant|inbox|chat|draft|question)/.test(r)) return 'Para preguntarle a Alexia, elige un plan';
  if (/(receta|recipe|plato|menu|carta|costeo|ingredient|catalog|preparacion|import)/.test(r)) return 'Para costear platos, elige un plan';
  return 'Para seguir sumando, elige un plan';
}

/**
 * Días que la sincronización nocturna del punto de venta recupera como mucho
 * cuando la última buena quedó atrás (p. ej. días sin plan). La manual llega a 92.
 */
export const DIAS_DE_RECUPERACION_VENTAS = 31;

/** Mensaje de la API cuando la prueba terminó y se intenta guardar algo. */
export const MENSAJE_PRUEBA_TERMINADA =
  'Tu prueba terminó. Tus datos siguen aquí y puedes verlos y descargarlos; para seguir sumando, elige un plan.';

/** Mensaje de la API cuando el plan está cancelado (se dio de baja) y se intenta guardar algo. */
export const MENSAJE_PLAN_CANCELADO =
  'Tu plan está cancelado. Tus datos siguen aquí y puedes verlos y descargarlos; para seguir guardando, elige un plan.';

/** El mensaje que toca a una cuenta sin plan: el de la baja o el de la prueba terminada. */
export function mensajeSinPlan(tenant: TenantEnPrueba | null | undefined): string {
  if (planEnPausa(tenant)) return mensajePlanEnPausa(tenant?.pausedUntil);
  return planCancelado(tenant) ? MENSAJE_PLAN_CANCELADO : MENSAJE_PRUEBA_TERMINADA;
}
